<script setup lang="ts">
import { Order } from '@/@types/order'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import CardContent from '@/components/ui/card/CardContent.vue'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head, Link, router, useForm } from '@inertiajs/vue3'

import { AlertCircle, ArrowLeftCircle, CheckCircle, Download, Loader2 } from 'lucide-vue-next'
import { computed, watch } from 'vue'

interface TicketAmounts {
    full_pass: number
    full_content: number
    digital: number
}

interface ImportParticipants {
    order_id: number
    errors: string[]
    rows_errors: string[]
    valid_records: number
    ticket_amounts: TicketAmounts
    valid_amounts: TicketAmounts
    available_tickets: number
    upload_id: number
    processed: boolean
    imported: boolean | null
    will_update_count: number
}

const props = defineProps<{
    order: Order
    import_result?: ImportParticipants
}>()

const form = useForm({
    file: null as File | null,
    upload_id: null as number | null,
    confirm_import: false as boolean,
})

const isFirstStep = computed(() => !props.import_result?.processed)
const isSecondStep = computed(() => props.import_result?.processed && !props.import_result?.imported)
const isCompleted = computed(() => props.import_result?.imported === true)

function uploadFile() {
    form.confirm_import = false
    form.upload_id = null

    form.post(`/admin/orders/${props.order.id}/import_participants`, {
        only: ['order', 'import_result', 'flash'],
        preserveState: true,
    })
}

function confirmImport() {
    form.confirm_import = true
    form.upload_id = props.import_result?.upload_id || null

    form.post(`/admin/orders/${props.order.id}/import_participants`, {
        only: ['order', 'import_result', 'flash'],
        preserveState: true,
    })
}

function handleFileChange(event: Event) {
    const target = event.target as HTMLInputElement
    const fileToUpload = target.files?.[0]
    if (fileToUpload) {
        form.file = fileToUpload
        form.upload_id = null
        form.confirm_import = false
    }
}

function resetImport() {
    form.file = null
    form.upload_id = null
    form.confirm_import = false
    form.reset()

    router.reload()
}

watch(
    () => form.file,
    () => {
        form.errors.file = undefined
        form.upload_id = null
        form.confirm_import = false
    },
)
</script>

<template>
    <Head title="Importar Participantes por Planilha" />
    <LightLayout>
        <main class="mx-auto max-w-4xl p-4">
            <div>
                <section class="flex items-center gap-2">
                    <Link :href="`/admin/orders/${props.order.id}`" aria-label="Back to order" class="focus:outline-none">
                        <ArrowLeftCircle class="h-8 w-8 text-slate-800" stroke-width="1.2" />
                    </Link>

                    <h1 class="text-2xl font-semibold text-gray-800">Importar Participantes por Planilha</h1>
                </section>
                <p class="mt-2 text-sm text-slate-500">
                    Faça o upload de uma planilha no formato .XLSX com os dados dos participantes para agilizar a vinculação de ingressos. A
                    importação é feita em duas etapas: validação e confirmação.
                </p>
            </div>

            <section class="mt-6">
                <Card v-if="isFirstStep" class="pt-6">
                    <CardHeader>
                        <CardTitle class="flex items-center gap-2">
                            <span class="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-600">1</span>
                            Upload e Validação do Arquivo
                        </CardTitle>
                        <CardDescription>Selecione o arquivo .XLSX com os dados dos participantes para validação</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button variant="outline"as="a":href="`/admin/orders/${props.order.id}/participants_import_template`"target="_blank"rel="noopener noreferrer"downloadclass="mb-4">
                            <Download class="mr-2 h-4 w-4" /> Baixar modelo .XLSX
                        </Button>
                        <form @submit.prevent="uploadFile" class="mt-4 flex flex-col" id="upload-form">
                            <Label required>Selecionar arquivo .XLSX</Label>
                            <Input
                                id="file-input"
                                type="file"
                                accept=".xlsx"
                                @change="handleFileChange"
                                required
                                class="!border-input !mt-1 flex !rounded-md !border !p-2 !text-sm [all:unset]"
                            />
                            <p v-if="form.errors.file" class="text-destructive mt-1 text-sm">{{ form.errors.file }}</p>
                        </form>

                        <div class="mt-4 flex items-center gap-2">
                            <Button :as="Link" :href="`/admin/orders/${props.order.id}`" variant="outline">Cancelar</Button>
                            <Button type="submit" form="upload-form" :disabled="form.processing || !form.file">
                                <template v-if="!form.processing"> Validar Arquivo </template>
                                <template v-else>
                                    <Loader2 class="mr-2 h-4 w-4 animate-spin" />
                                    Validando...
                                </template>
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                <Card v-if="isSecondStep" class="pt-6">
                    <CardHeader>
                        <CardTitle class="flex items-center gap-2">
                            <span class="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 text-sm font-medium text-green-600"
                                >2</span
                            >
                            Revisão e Confirmação
                        </CardTitle>
                        <CardDescription>Revise os resultados da validação e confirme a importação</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div class="mb-6 rounded-lg bg-slate-50 p-4">
                            <h3 class="mb-3 font-medium text-slate-900">Resumo da Validação</h3>

                            <div v-if="props.import_result?.ticket_amounts" class="mt-4">
                                <h4 class="mb-2 text-sm font-medium text-slate-700">
                                    Ingressos disponíveis para importação ({{ props.import_result?.available_tickets || 0 }})
                                </h4>
                                <div class="grid grid-cols-3 gap-2 text-xs">
                                    <div class="rounded border bg-white p-2">
                                        <div class="text-slate-600">Full Pass</div>
                                        <div class="font-medium">{{ props.import_result.ticket_amounts.full_pass }}</div>
                                    </div>
                                    <div class="rounded border bg-white p-2">
                                        <div class="text-slate-600">Conteúdo Total</div>
                                        <div class="font-medium">{{ props.import_result.ticket_amounts.full_content }}</div>
                                    </div>
                                    <div class="rounded border bg-white p-2">
                                        <div class="text-slate-600">Palestras Gravadas</div>
                                        <div class="font-medium">{{ props.import_result.ticket_amounts.digital }}</div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="props.import_result?.valid_amounts" class="mt-4">
                                <h4 class="mb-2 text-sm font-medium text-green-600 text-slate-700" style="color: green">
                                    Aprovados para importação ({{ props.import_result?.valid_records || 0 }})
                                </h4>
                                <h4 class="mt-4 mb-2 text-sm font-medium text-green-600 text-slate-700">Ingressos</h4>
                                <div class="grid grid-cols-3 gap-2 text-xs">
                                    <div v-if="props.import_result.valid_amounts.full_pass" class="rounded border bg-white p-2">
                                        <div class="text-slate-600">Full Pass</div>
                                        <div class="font-medium">{{ props.import_result.valid_amounts.full_pass }}</div>
                                    </div>
                                    <div v-if="props.import_result.valid_amounts.full_content" class="rounded border bg-white p-2">
                                        <div class="text-slate-600">Conteúdo Total</div>
                                        <div class="font-medium">{{ props.import_result.valid_amounts.full_content }}</div>
                                    </div>
                                    <div v-if="props.import_result.valid_amounts.digital" class="rounded border bg-white p-2">
                                        <div class="text-slate-600">Palestras Gravadas</div>
                                        <div class="font-medium">{{ props.import_result.valid_amounts.digital }}</div>
                                    </div>
                                </div>
                                <h4 class="mt-4 mb-2 text-sm font-medium text-green-600 text-slate-700">Participantes</h4>
                                <div class="font-medium">
                                    <span class="text-slate-600">{{
                                        props.import_result.valid_records - props.import_result.will_update_count
                                    }}</span>
                                    novo(s)<br />
                                    <span class="text-slate-600">{{ props.import_result.will_update_count }}</span> existente(s)<br />
                                </div>
                            </div>
                        </div>

                        <div v-if="props.import_result?.rows_errors && props.import_result.rows_errors.length > 0" class="mb-6">
                            <div class="mb-3 flex items-center gap-2">
                                <AlertCircle class="h-5 w-5 text-red-500" />
                                <h3 class="font-medium text-red-800">Erros encontrados ({{ props.import_result.rows_errors.length }})</h3>
                            </div>
                            <div class="max-h-40 space-y-2 overflow-y-auto">
                                <div
                                    v-for="(error, index) in props.import_result.rows_errors"
                                    :key="index"
                                    class="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                                >
                                    {{ error }}
                                </div>
                            </div>
                        </div>

                        <div v-if="props.import_result?.errors && props.import_result.errors.length > 0" class="mb-6">
                            <div class="mb-3 flex items-center gap-2">
                                <AlertCircle class="h-5 w-5 text-red-500" />
                                <h3 class="font-medium text-red-800">Erros gerais</h3>
                            </div>
                            <div class="space-y-2">
                                <div
                                    v-for="(error, index) in props.import_result.errors"
                                    :key="index"
                                    class="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                                >
                                    {{ error }}
                                </div>
                            </div>
                        </div>

                        <div class="flex items-center gap-2">
                            <Button variant="outline" @click="resetImport">Novo Arquivo</Button>
                            <Button :as="Link" :href="`/admin/orders/${props.order.id}`" variant="outline">Cancelar</Button>

                            <Dialog v-if="props.import_result?.valid_records && props.import_result.valid_records > 0">
                                <DialogTrigger as-child>
                                    <Button :disabled="form.processing" class="bg-green-600 hover:bg-green-700">
                                        <CheckCircle class="mr-2 h-4 w-4" />
                                        Confirmar Importação
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Confirmar Importação de Participantes</DialogTitle>
                                        <DialogDescription>
                                            Você está prestes a importar {{ props.import_result?.valid_records }} participante(s). Esta ação não pode
                                            ser desfeita.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <DialogFooter>
                                        <DialogClose as-child>
                                            <Button variant="outline">Cancelar</Button>
                                        </DialogClose>
                                        <Button @click="confirmImport" :disabled="form.processing" class="bg-green-600 hover:bg-green-700">
                                            <template v-if="!form.processing"> Confirmar Importação </template>
                                            <template v-else>
                                                <Loader2 class="mr-2 h-4 w-4 animate-spin" />
                                                Importando...
                                            </template>
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </div>
                    </CardContent>
                </Card>

                <Card v-if="isCompleted" class="pt-6">
                    <CardHeader>
                        <CardTitle class="flex items-center gap-2 text-green-600">
                            <CheckCircle class="h-6 w-6" />
                            Importação Concluída com Sucesso!
                        </CardTitle>
                        <CardDescription>Os participantes foram importados e vinculados aos ingressos</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div class="mb-4 rounded-lg bg-green-50 p-4">
                            <p class="text-green-800">{{ props.import_result?.valid_records }} participante(s) foram importados com sucesso.</p>
                        </div>

                        <div class="flex items-center gap-2">
                            <Button :as="Link" :href="`/admin/orders/${props.order.id}`"> Ver Pedido </Button>
                            <Button @click="resetImport" variant="outline"> Importar Mais Participantes </Button>
                        </div>
                    </CardContent>
                </Card>
            </section>
        </main>
    </LightLayout>
</template>
